<crowdctrl-unauthenticated-page [pageTitle]="pageTitle" [showFooter]="false" defaultHref="/about">
  <div class="about-project">
    <crowdctrl-unauth-page-intro>
      <h1 crowdctrlUnauthIntroTitle>How it all works</h1>
      <p crowdctrlUnauthIntroLead>
        A narrative walkthrough from first contact to a busy session. If you prefer grouped
        highlights, see
        <a
          href="/about/features"
          class="about-project__inline-link"
          (click)="unauthNav.forward($event, '/about/features')"
          >Features</a
        >.
      </p>
    </crowdctrl-unauth-page-intro>

    <section class="about-project__section" aria-labelledby="hiw-onboarding">
      <h2 id="hiw-onboarding" class="about-project__heading">1. Onboarding your organisation</h2>
      <p class="about-project__text">
        It starts when someone from your group
        <a
          href="/register-interest"
          class="about-project__inline-link"
          (click)="unauthNav.forward($event, '/register-interest')"
          >registers interest</a
        >
        so CrowdCTRL knows who you are and what you want to run. After review, an approval gives
        your <strong>team lead</strong> a tenancy: the administrative home for your team or
        organisation, separate from other customers, with its own users, definitions, and data.
      </p>
      <p class="about-project__text">
        <strong>CrowdCTRL Manager</strong> is where teams maintain the tenancy: membership,
        workspace blueprints, and the rules sessions run under. Live session work sits in
        <strong>CrowdCTRL Service</strong>, intake in <strong>CrowdCTRL Forms</strong>, and wall
        displays in <strong>CrowdCTRL Boards</strong>, each as a dedicated CrowdCTRL app.
      </p>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-signin">
      <h2 id="hiw-signin" class="about-project__heading">2. Signing in</h2>
      <p class="about-project__text">
        After your <strong>team lead</strong> has added you, you authenticate on the
        <a
          href="/login"
          class="about-project__inline-link"
          (click)="unauthNav.forward($event, '/login')"
          >login page</a
        >
        with the account your <strong>team lead</strong> registered for you. If you are invited to
        more than one tenancy, you choose which organisation you are acting for before you open
        workspaces, so personal and institutional contexts stay separated.
      </p>
    </section>

    <section
      class="about-project__section about-project__section--four-apps about-project__section--hiw-app-cards"
      aria-labelledby="hiw-apps"
    >
      <h2 id="hiw-apps" class="about-project__heading">3. Four apps in your tenancy</h2>
      <p class="about-project__text features-apps-intro">
        A tenancy is not a single website. You get up to four CrowdCTRL web apps, each focused on
        one kind of work. The account your <strong>team lead</strong> registered for you can sign
        into all of them, but most people only use the one that matches their role that day. Each
        card opens more detail for that app.
      </p>

      <ul class="features-apps-grid" aria-label="CrowdCTRL apps">
        <li class="hiw-app-tile-item">
          <crowdctrl-service-nav-tile
            expandedCard
            labelStrong
            route="/about/how-it-works/manager"
            label="CrowdCTRL Manager"
            icon="business-outline"
          >
            <p>
              Define queues, forms, boards, and policies inside workspace blueprints, and manage
              membership and groups before the room opens.
            </p>
          </crowdctrl-service-nav-tile>
        </li>
        <li class="hiw-app-tile-item">
          <crowdctrl-service-nav-tile
            expandedCard
            labelStrong
            route="/about/how-it-works/service"
            label="CrowdCTRL Service"
            icon="briefcase-outline"
          >
            <p>
              Live session operations: queues, requests, overview, and controls, while teaching or
              in the lab. Remote control of <strong>CrowdCTRL Forms</strong> and
              <strong>CrowdCTRL Boards</strong> without signing into those apps separately.
            </p>
          </crowdctrl-service-nav-tile>
        </li>
        <li class="hiw-app-tile-item">
          <crowdctrl-service-nav-tile
            expandedCard
            labelStrong
            route="/about/how-it-works/forms"
            label="CrowdCTRL Forms"
            icon="reader-outline"
          >
            <p>
              A separate intake app. Students use public forms that teachers enable for each
              session, without signing in. Tablet kiosks may sit unattended in the room. You set up
              QR codes and NFC tags in <strong>CrowdCTRL Service</strong>; students use those same
              codes and tags in <strong>CrowdCTRL Forms</strong> to reach the right intake.
            </p>
          </crowdctrl-service-nav-tile>
        </li>
        <li class="hiw-app-tile-item">
          <crowdctrl-service-nav-tile
            expandedCard
            labelStrong
            route="/about/how-it-works/boards"
            label="CrowdCTRL Boards"
            icon="tv-outline"
          >
            <p>
              Unattended wall or TV display of live queue state so the room can self-orient at a
              glance. When teachers call students in <strong>CrowdCTRL Service</strong>, the board
              shows those calls too.
            </p>
          </crowdctrl-service-nav-tile>
        </li>
      </ul>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-workspaces">
      <h2 id="hiw-workspaces" class="about-project__heading">4. Workspaces and people</h2>
      <p class="about-project__text">
        A workspace is the container your team picks for a slice of real life: a subject, a lab
        programme, a help desk, or a venue. Workspaces are created from the workspace definitions
        you curate in <strong>CrowdCTRL Manager</strong>, which keeps naming, queues, and rules
        aligned across weeks and terms.
      </p>
      <p class="about-project__text">
        Not everyone in the tenancy belongs to every workspace. The
        <strong>team lead</strong> registers or invites other team members into the workspaces they
        need. Membership is the boundary: people only see sessions and past activity for workspaces
        they belong to.
      </p>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-config">
      <h2 id="hiw-config" class="about-project__heading">5. Queues and rules</h2>
      <p class="about-project__text">
        The shape of each queue, any intake forms, display boards, and the rules are edited on the
        workspace definition in <strong>CrowdCTRL Manager</strong>. Those rules cover who may join,
        how requests are ordered, and what limits apply. The definition is versioned, so older
        workspaces stay on the version they started with; new workspaces follow the latest publish.
      </p>
      <p class="about-project__text">
        That separation matters: a workspace does not chase every new publish, so behaviour stays
        steady for the people using that workspace. It also means fairness is not a personality test
        for whoever is on duty; it is expressed in policies everyone can see.
      </p>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-sessions">
      <h2 id="hiw-sessions" class="about-project__heading">6. Sessions</h2>
      <p class="about-project__text">
        A session is the live episode inside a workspace: the studio block for that slot, this
        consultation window, or that exam help block. Opening a session marks the workspace as live
        for that run, so joins, requests, and staff actions land in one shared timeline instead of
        splitting across the room with no single shared stretch of time.
      </p>
      <p class="about-project__text">
        When the session ends, you get a clean break. The next class starts fresh with the same
        underlying rules but a new queue state, which is easier to explain to students and easier to
        audit later.
      </p>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-running">
      <h2 id="hiw-running" class="about-project__heading">7. During the class or lab</h2>
      @if (isServiceApp) {
        <p class="about-project__text">
          Students who need help place a request in the queue that matches their situation. Most
          open a form from a QR code, a shared URL, or an NFC tap; if not, a teacher queues them
          with manual intake in <strong>CrowdCTRL Service</strong>. The platform orders work using
          the policies you configured, which reduces the scramble of hand signals and overlapping
          conversations at the bench.
        </p>
        <p class="about-project__text">
          Staff watch overview and controls to see depth, use manual intake in
          <strong>CrowdCTRL Service</strong> when someone is not coming through the form path, post
          alerts if the room needs a broadcast, and step requests through states as they are
          answered. Students see their own position and updates, so they can step back and work
          until it is their turn.
        </p>
      } @else if (isFormsApp) {
        <p class="about-project__text">
          Students who need help usually join a queue from a QR code, a shared URL, or an NFC tap in
          <strong>CrowdCTRL Forms</strong>. When someone does not use that path, staff can add them
          with manual intake in <strong>CrowdCTRL Service</strong>. The platform orders work using
          the policies configured in your tenancy, which reduces the scramble of hand signals and
          overlapping conversations at the bench.
        </p>
        <p class="about-project__text">
          While signed into <strong>CrowdCTRL Forms</strong>, you choose workspaces and sessions,
          open the right intake, and share links so the class lands in the correct queue. Floor
          staff use <strong>CrowdCTRL Service</strong> for overview, controls, alerts, and moving
          requests through states. Students see their own place and updates in
          <strong>CrowdCTRL Forms</strong> where your tenancy enables it, so they can wait without
          crowding the desk.
        </p>
      } @else {
        <p class="about-project__text">
          Students who need help place a request in the queue that matches their situation. Most
          open a form from a QR code, a shared URL, or an NFC tap; if not, staff may queue them with
          manual intake in <strong>CrowdCTRL Service</strong>. The platform orders work using the
          policies you configured.
        </p>
        <p class="about-project__text">
          Staff use <strong>CrowdCTRL {{ marketing.appName }}</strong> alongside other CrowdCTRL
          apps: overview and controls, intake through <strong>CrowdCTRL Forms</strong>, and room
          displays on <strong>CrowdCTRL Boards</strong> according to role and how your organisation
          runs each session.
        </p>
      }
    </section>

    <section
      class="about-project__section about-project__section--hiw-scenario"
      aria-labelledby="hiw-scenario-small"
    >
      <h2 id="hiw-scenario-small" class="about-project__heading">8. Small class, one teacher</h2>
      <div class="hiw-scenario-cards">
        <article class="hiw-scenario-card" aria-labelledby="hiw-scenario-before-title">
          <h3 id="hiw-scenario-before-title" class="hiw-scenario-card__title">Before</h3>
          <div class="hiw-scenario-card__body">
            <p class="about-project__text">
              Nick is the only teacher in a small class. While he helps Matthew at the bench, there
              is no second pair of eyes on the rest of the room.
            </p>
            <p class="about-project__text">
              Nick thought Ben was next, but Ben must have stepped out. Nadia was after Ben in line,
              yet she is caught up in her own work and not watching Nick for cues. The other
              students have no idea she was the one who followed Ben. Nick tries to piece together
              who asked for help when, and he is frazzled. He asks the room who might have been
              next, hoping the class is honest.
            </p>
            <p class="about-project__text">
              Nadia keeps waiting at her desk. Nobody puts her case forward, and Nick has no list to
              prove she was right after Ben, so she is stuck while the room guesses.
            </p>
            <p class="about-project__text">
              Later, Ben returns while Nick is helping Oliver. Ben expects Nick to switch to him
              straight away, and Nick has no fair record to point to.
            </p>
            <ul class="about-project__list hiw-scenario-feelings-list" aria-label="How things feel">
              <li>
                The class feels interrupted: attention keeps snapping to the queue debate instead of
                staying with the lesson.
              </li>
              <li>Nick feels stretched and on the back foot.</li>
              <li>Ben feels skipped or wronged.</li>
              <li>Nadia feels invisible.</li>
              <li>
                Matthew loses the thread of the help he was getting because the floor keeps turning
                into a debate about the line.
              </li>
              <li>Oliver gets the same disruption when it is his turn at the bench.</li>
            </ul>
          </div>
        </article>
        <article class="hiw-scenario-card" aria-labelledby="hiw-scenario-after-title">
          <h3 id="hiw-scenario-after-title" class="hiw-scenario-card__title">After</h3>
          <div class="hiw-scenario-card__body">
            <p class="about-project__text">
              Ahead of class, Nick programs NFC tags using <strong>CrowdCTRL Service</strong> on
              Android. That does not have to happen in the classroom. He can do it from his phone
              wherever he is preparing.
            </p>
            <p class="about-project__text">
              When he gets to class, he places those tags around the room so students can tap to the
              same intake. He also brings a portable tablet kiosk running
              <strong>CrowdCTRL Forms</strong> for students who cannot tap. From
              <strong>CrowdCTRL Service</strong> on his phone, he enables the intake form for the
              session so that the tags and kiosk are live. <strong>CrowdCTRL Boards</strong> on the
              class screen can show that same intake as a QR code, so students have another way to
              queue from their seats without finding a tag or the kiosk.
            </p>
            <p class="about-project__text">
              Nick checks <strong>CrowdCTRL Service</strong> and sees that Ben is next. He calls for
              Ben using <strong>CrowdCTRL Boards</strong> on the class screen. Ben does not respond,
              so Nick gives Ben five minutes and snoozes Ben's request in
              <strong>CrowdCTRL Service</strong>. Nick then calls for Nadia, who is next up.
            </p>
            <p class="about-project__text">
              He can let go of queue management in his head. Between the lesson, the bench, and
              reading the room, he already has more than enough to worry about and focus on.
            </p>
            <ul
              class="about-project__list hiw-scenario-feelings-list"
              aria-label="How things feel now"
            >
              <li>
                The class keeps attention on the lesson; the queue is in the apps and on the screen,
                not in a shout across the room.
              </li>
              <li>
                Nick trusts <strong>CrowdCTRL Service</strong> and
                <strong>CrowdCTRL Boards</strong> for order, uses snooze when someone is missing,
                and does not have to poll the room.
              </li>
              <li>
                Ben gets a clear call on the board when it is his turn, and a timed pause if he is
                slow to answer.
              </li>
              <li>Nadia is visibly next once Ben is handled, so she is not left guessing.</li>
              <li>
                Matthew gets bench time with Nick without the room turning into a queue inquest.
              </li>
              <li>Oliver gets the same when it is his turn.</li>
            </ul>
          </div>
        </article>
      </div>
    </section>

    <section
      class="about-project__section about-project__section--hiw-scenario"
      aria-labelledby="hiw-scenario-large"
    >
      <h2 id="hiw-scenario-large" class="about-project__heading">
        9. Large class, multiple teachers
      </h2>
      <div class="hiw-scenario-cards">
        <article class="hiw-scenario-card" aria-labelledby="hiw-scenario-large-before-title">
          <h3 id="hiw-scenario-large-before-title" class="hiw-scenario-card__title">Before</h3>
          <div class="hiw-scenario-card__body">
            <p class="about-project__text">
              James leads a lectorial for about two hundred to three hundred students. Dan and Micah
              are tutors in the room with him.
            </p>
            <p class="about-project__text">
              Students start informal physical queues near the teachers. James knows Dan is best for
              technical issues and Micah for class activities. Someone may explain at the start who
              to approach for what, but most students forget and follow whichever line looks busy or
              short.
            </p>
            <p class="about-project__text">
              The visible lines become the signal for who counts as waiting, so students leave their
              desks to stand in those snakes instead of staying with their work.
            </p>
            <p class="about-project__text">
              Dan and Micah sometimes help someone at their desk, then look up and see many raised
              hands. They cannot tell which hand is for which topic, or which hand went up first.
            </p>
            <ul class="about-project__list hiw-scenario-feelings-list" aria-label="What goes wrong">
              <li>
                The class loses time and focus: bodies in lines and arms in the air replace a clear,
                fair order.
              </li>
              <li>
                James loses the match between student need and the right tutor once informal queues
                take over.
              </li>
              <li>
                Dan and Micah work blind to order and topic when they only have a crowd of hands to
                read.
              </li>
              <li>Pushy students can edge ahead of timid ones.</li>
              <li>
                Other students stop asking if they believe they have no real chance at a fair turn.
              </li>
              <li>
                Some trade listening and practice time to guard a spot in a line instead of
                learning.
              </li>
              <li>
                If someone steps out of a line, they may lose their place with no fair way to step
                back in.
              </li>
            </ul>
          </div>
        </article>
        <article class="hiw-scenario-card" aria-labelledby="hiw-scenario-large-after-title">
          <h3 id="hiw-scenario-large-after-title" class="hiw-scenario-card__title">After</h3>
          <div class="hiw-scenario-card__body">
            <p class="about-project__text">
              Ahead of class, Dan programs NFC tags using <strong>CrowdCTRL Service</strong> on
              Android, the same kind of prep a single teacher might do alone. He brings them to the
              lectorial and places them around the room so taps are spread out. Nobody has to mob
              one desk or doorway, which eases crowding and the rush at the front.
            </p>
            <p class="about-project__text">
              The workspace has four queues: one for any teacher, one for James, one for Dan, and
              one for Micah. The intake form lets students line up in whichever queue fits. Some
              want Dan for work carried over from a previous class, others want the lecturer for a
              specific question, and others join the any-teacher line for whoever is free first. Dan
              also sets up a portable tablet kiosk at the front running
              <strong>CrowdCTRL Forms</strong> for students who cannot tap a tag on their phone. The
              class screen on <strong>CrowdCTRL Boards</strong> can show the intake form as a QR
              code too, so anyone can join the line from their seat without leaving for a tag or the
              kiosk.
            </p>
            <p class="about-project__text">
              When class begins, James opens <strong>CrowdCTRL Service</strong> and sets his
              ordering to pull from both the any-teacher queue and the James queue. Dan does the
              same for the any-teacher pool plus the Dan queue; Micah matches that for the shared
              pool and the Micah queue. Each teacher works their own ordered list instead of reading
              the room for hands.
            </p>
            <p class="about-project__text">
              They take turns by calling the next student on <strong>CrowdCTRL Boards</strong> for
              the whole room. The projection might show that request 234 should approach James, that
              Dan is ready for request 871, and that Micah is already helping request 2319. Request
              234 meets James. The call for request 871 goes unanswered, so Dan snoozes it in
              <strong>CrowdCTRL Service</strong> and calls request 981 next.
            </p>
            <ul
              class="about-project__list hiw-scenario-feelings-list"
              aria-label="How things feel now"
            >
              <li>
                Students stay at their desks more often; the visible order lives in the apps and on
                the screen, not in snakes of bodies by the front.
              </li>
              <li>
                James keeps a clearer match between what each student asked for and the right tutor,
                because intake captured the queue they chose.
              </li>
              <li>
                Dan and Micah see fair sequence and can call the next person instead of guessing
                which hand was first.
              </li>
              <li>
                Snooze gives missing students a timed pause without losing the thread for everyone
                else.
              </li>
              <li>
                Quieter students are less likely to be shouldered aside by whoever pushed closest to
                the desk.
              </li>
              <li>
                The room spends less class time on line-keeping and more on the actual lectorial and
                practice.
              </li>
            </ul>
          </div>
        </article>
      </div>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-fair-service-visible">
      <h2 id="hiw-fair-service-visible" class="about-project__heading">
        10. Keeping fair service visible
      </h2>
      <p class="about-project__text">
        Intake and <strong>CrowdCTRL Boards</strong> are what people see first. The same tenancy
        still has to resolve competing queues fairly, make waits and hot lines visible to staff, and
        give each student a straight answer on their own place so the teacher is not the only source
        of truth.
      </p>
      <ul class="about-project__list" aria-label="Fair policies, waits, and student visibility">
        <li>
          In <strong>CrowdCTRL Service</strong>, teachers and <strong>team leads</strong> define
          custom serving policies: which queues take precedence when several are busy, how those
          queues are grouped, and aging so long waits bubble up instead of starving behind a line
          that always looks more urgent.
        </li>
        <li>
          Overview and time views in <strong>CrowdCTRL Service</strong> make wait, depth, and
          service duration legible: who has been waiting how long, which queues are backing up, and
          how help sessions actually ran. Afterward you can read demand spikes and bottlenecks in
          the same tenancy that held the live session.
        </li>
        <li>
          Students already see their own place and updates in <strong>CrowdCTRL Forms</strong>, so
          they can step back and work without hovering at the front or waiting for another shout
          across the room.
        </li>
      </ul>
    </section>

    <section class="about-project__section" aria-labelledby="hiw-more">
      <h2 id="hiw-more" class="about-project__heading">Also see</h2>
      <p class="about-project__text">
        See
        <a
          href="/about/features"
          class="about-project__inline-link"
          (click)="unauthNav.forward($event, '/about/features')"
          >Features</a
        >
        for grouped highlights. For stack and architecture, see
        <a
          href="/about/technical"
          class="about-project__inline-link"
          (click)="unauthNav.forward($event, '/about/technical')"
          >Technical details</a
        >.
      </p>
    </section>

    <a href="/about" class="about-project__back-link" (click)="unauthNav.forward($event, '/about')"
      >Back to About</a
    >
  </div>
</crowdctrl-unauthenticated-page>
